import { createInertiaApp, router } from '@inertiajs/react'
import { createElement, ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import RootLayout from '../layouts/RootLayout'

// Temporary type definition, until @inertiajs/react provides one
type ResolvedComponent = {
  default: ReactNode
  layout?: (page: ReactNode) => ReactNode
}

// Remove initial HTML loader when React is ready
const removeInitialLoader = () => {
  const loader = document.getElementById('initial-loader')
  if (loader) loader.remove()
}

createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Progress bar configuration (for navigation between pages)
  // see https://inertia-rails.dev/guide/progress-indicators
  progress: {
    delay: 0,
    color: '#3b82f6',
    includeCSS: true,
    showSpinner: false,
  },

  resolve: (name) => {
    // Lazy loading - each page is a separate chunk, loaded on demand
    const pages = import.meta.glob<ResolvedComponent>('../pages/**/*.tsx')

    const importPage = pages[`../pages/${name}.tsx`]
    if (!importPage) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
      throw new Error(`Page not found: ${name}`)
    }

    // Return the import function and apply layout after resolution
    return importPage().then((page) => {
      // Apply RootLayout to all pages to provide Intercom
      page.default.layout ||= (page) => createElement(RootLayout, null, page)
      return page
    })
  },

  setup({ el, App, props }) {
    if (el) {
      // Remove HTML loader and render React app
      removeInitialLoader()
      createRoot(el).render(createElement(App, props))
    } else {
      console.error(
        'Missing root element.\n\n' +
          'If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n' +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
      )
    }
  },
})

// Force reload on browser back/forward navigation to prevent viewing cached authenticated pages after logout
window.addEventListener('popstate', (event) => {
  // Use window.location.reload() instead of router.reload() to bypass Inertia
  // This will make a full page request that Rails can handle with proper redirects
  window.location.reload()
})
